'use client';
import { Fragment as _Fragment, jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useEffect, useMemo, useState, } from 'react';
import { safeJsonStringify } from './safe-json.js';
import { runtimeConfigSchema } from './schema.js';
const RuntimeConfigContext = createContext(null);
const writeToWindow = (config) => {
    if (typeof window !== 'undefined') {
        window.__CONFIG__ = config;
    }
};
export function RuntimeConfigProvider(props) {
    const initialConfig = props.initialConfig ?? null;
    const fetchUrl = props.fetchUrl ?? null;
    const [fetched, setFetched] = useState(null);
    useEffect(() => {
        if (initialConfig)
            writeToWindow(initialConfig);
    }, [initialConfig]);
    useEffect(() => {
        if (!fetchUrl)
            return;
        let cancelled = false;
        // Abort after 10s so a hung edge function doesn't leave the app on the
        // fallback indefinitely. The AbortError surfaces via the .catch below.
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 10_000);
        fetch(fetchUrl, {
            credentials: 'same-origin',
            signal: controller.signal,
        })
            .then((res) => {
            if (!res.ok) {
                throw new Error(`RuntimeConfig fetch failed: ${res.status} ${res.statusText}`);
            }
            return res.json();
        })
            .then((data) => {
            const parsed = runtimeConfigSchema.parse(data);
            if (cancelled)
                return;
            writeToWindow(parsed);
            setFetched(parsed);
        })
            .catch((err) => {
            if (cancelled)
                return;
            // Surface the failure: app code that depends on config should not
            // silently get stale/empty values.
            console.error('[runtime-config] failed to fetch', err);
        })
            .finally(() => {
            clearTimeout(timeoutId);
        });
        return () => {
            cancelled = true;
            clearTimeout(timeoutId);
            controller.abort();
        };
    }, [fetchUrl]);
    const value = useMemo(() => initialConfig ?? fetched, [initialConfig, fetched]);
    if (!value) {
        return _jsx(_Fragment, { children: props.fallback });
    }
    return (_jsx(RuntimeConfigContext.Provider, { value: value, children: props.children }));
}
export function useRuntimeConfig() {
    const ctx = useContext(RuntimeConfigContext);
    if (!ctx) {
        throw new Error('useRuntimeConfig must be used inside RuntimeConfigProvider');
    }
    return ctx;
}
/**
 * Like {@link useRuntimeConfig}, but returns `null` outside a provider instead
 * of throwing. For optional reads (feature overrides) in components that are
 * also rendered by unit tests without the provider.
 */
export function useOptionalRuntimeConfig() {
    return useContext(RuntimeConfigContext);
}
export function RuntimeConfigScript({ config, nonce, }) {
    const html = `window.__CONFIG__=${safeJsonStringify(config)};`;
    return (_jsx("script", { nonce: nonce, 
        // biome-ignore lint/security/noDangerouslySetInnerHtml: required to inject runtime config before hydration
        dangerouslySetInnerHTML: { __html: html } }));
}
export { runtimeConfigSchema } from './schema.js';
//# sourceMappingURL=client.js.map